import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { loginFx, fetchCurrentUserFx } from '../store/auth';

import {
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  Box,
} from '@mui/material';

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, isFetchingUser] = useUnit([loginFx.pending, fetchCurrentUserFx.pending]);
  const isProcessing = isLoading || isFetchingUser;
  const navigate = useNavigate();
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    const applyFontFix = () => {
      [usernameRef.current, passwordRef.current].forEach((input) => {
        if (input) {
          input.style.fontSize = '18px';
          input.style.lineHeight = '1.2';
        }
      });
    };
    applyFontFix();
    const raf = requestAnimationFrame(applyFontFix);
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Пожалуйста, заполните все поля');
      return;
    }

    try {
      await loginFx({ username, password });
      await fetchCurrentUserFx();
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      setError('Неверное имя пользователя или пароль');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!isProcessing) {
            handleSubmit(e);
          }
        }
      }}
    >
      <Box sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
        <Stack spacing={{ xs: 3, sm: 4, md: 5 }}>
          <Typography
            component="h1"
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: 'text.primary',
            }}
          >
            Вход в систему
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Имя пользователя"
            variant="outlined"
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isProcessing}
            autoComplete="username"
            inputRef={usernameRef}
            inputProps={{
              style: {
                fontSize: '18px',
                lineHeight: '1.2',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--surface-elev)',
                borderRadius: '14px',
                '& fieldset': {
                  borderColor: 'rgba(100, 116, 139, 0.45)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(37, 99, 235, 0.6)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}22`,
                },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--muted-color)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--text-color)',
              },
              '& .MuiOutlinedInput-input': {
                padding: { xs: '12px 14px', sm: '14px 16px' },
                fontWeight: 500,
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
                color: 'var(--text-color)',
              },
              '& .MuiOutlinedInput-input:focus': {
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
              },
              '& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:focus': {
                WebkitTextFillColor: 'var(--text-color)',
                WebkitBoxShadow: '0 0 0 1000px var(--surface-elev) inset',
                transition: 'background-color 5000s ease-in-out 0s',
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
              },
            }}
          />

          <TextField
            label="Пароль"
            type="password"
            variant="outlined"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isProcessing}
            autoComplete="current-password"
            inputRef={passwordRef}
            inputProps={{
              style: {
                fontSize: '18px',
                lineHeight: '1.2',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--surface-elev)',
                borderRadius: '14px',
                '& fieldset': {
                  borderColor: 'rgba(100, 116, 139, 0.45)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(37, 99, 235, 0.6)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}22`,
                },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--muted-color)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--text-color)',
              },
              '& .MuiOutlinedInput-input': {
                padding: { xs: '12px 14px', sm: '14px 16px' },
                fontWeight: 500,
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
                color: 'var(--text-color)',
              },
              '& .MuiOutlinedInput-input:focus': {
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
              },
              '& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:focus': {
                WebkitTextFillColor: 'var(--text-color)',
                WebkitBoxShadow: '0 0 0 1000px var(--surface-elev) inset',
                transition: 'background-color 5000s ease-in-out 0s',
                fontSize: '18px !important',
                lineHeight: '1.2 !important',
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isProcessing}
            size="large"
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 700,
              height: 50,
              fontSize: { xs: '16px', sm: '20px' },
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 26px rgba(2, 6, 23, 0.45)'
                  : '0 14px 30px rgba(37, 99, 235, 0.28)',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Войти'}
          </Button>
        </Stack>
      </Box>
    </form>
  );
};

export default LoginForm;
